/**
 * QelliFaceAvatar — 2026-02 redesign (Iter226 + Iter227 polish).
 *
 * Renders the user-supplied flat Qelli portrait (face/headphones,
 * no wordmark, ~761×761 crop) with subtle CSS-driven life:
 *
 *   - Eye-blink overlay bars at the lens centres scaleY 0→1→0
 *     every 3-7s (1.4-2.8s while pulsing). 150ms closed dwell.
 *   - Smile-warmth glow over the built-in smile every 9-12s.
 *   - Attention "breathe" loop (4.2s) — Qelli scales 1→1.09→1
 *     while her drop-shadow halo brightens, then settles. This
 *     replaces the static cyan ring with a more organic call-out.
 *   - Echo ring — a thin cyan ring radiates outward in sync with
 *     the breathe peak so she's noticeable in peripheral vision.
 *
 * Public API:
 *   <QelliFaceAvatar size={104} pulsing={false} hovered={false} />
 */
import React, { useEffect, useState } from "react";


const QELLI_AVATAR_URL = "/assets/qelli-face.png";


export function QelliFaceAvatar({
  size      = 104,
  pulsing   = false,
  hovered   = false,
  waving    = false,         // Iter273.10 — fires the "hi 👋" head-tilt + bounce on mount
  className = "",
}) {
  // Eye-blink — random interval (3-7s normally, 1.4-2.8s while pulsing).
  const [blink, setBlink] = useState(false);
  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      setBlink(true);
      window.setTimeout(() => setBlink(false), 150);
      const next = pulsing
        ? 1400 + Math.random() * 1400
        : 3000 + Math.random() * 4500;
      window.setTimeout(tick, next);
    };
    const initial = window.setTimeout(tick, 800);
    return () => { stopped = true; window.clearTimeout(initial); };
  }, [pulsing]);

  // Smile-warmth glow — soft cyan halo over the mouth every ~10s.
  const [smileOn, setSmileOn] = useState(false);
  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      setSmileOn(true);
      window.setTimeout(() => setSmileOn(false), 1100);
      window.setTimeout(tick, 8500 + Math.random() * 4000);
    };
    const t = window.setTimeout(tick, 2400);
    return () => { stopped = true; window.clearTimeout(t); };
  }, []);

  // Eye centres on the 860×860 face crop (re-detected from the
  // Iter226 v4 source PNG):
  //   left  → (36%, 47%), right → (64%, 47%).
  const lidColor  = "#0FB7DA";
  const lidWidth  = Math.round(size * 0.18);
  const lidHeight = Math.round(size * 0.045);

  // While hovered or pulsing we pause the gentle breathe and crank
  // the halo up to "she's clearly waving at you" levels.
  // Iter273.10 — when `waving` is true we ALSO compose in the one-shot
  // qelli-wave gesture so the visitor gets a literal "hi 👋" tilt on
  // first mount of the welcome-back card. Combining two animations on
  // one element is fine — they're additive via `animation` shorthand.
  const baseAnim = (hovered || pulsing) ? "none" : "qelli-face-breathe 4.2s ease-in-out infinite";
  const waveAnim = waving ? "qelli-wave 1.6s cubic-bezier(0.42, 0, 0.58, 1) 1" : null;
  const breatheStyle = (hovered || pulsing)
    ? {
        transform: pulsing ? "scale(1.10)" : "scale(1.05)",
        filter: pulsing
          ? "drop-shadow(0 0 14px rgba(0,229,255,0.85)) drop-shadow(0 0 36px rgba(0,229,255,0.55))"
          : "drop-shadow(0 0 12px rgba(0,229,255,0.55)) drop-shadow(0 0 24px rgba(0,229,255,0.30))",
        transition: "transform 200ms ease, filter 200ms ease",
        animation: waveAnim || "none",
      }
    : {
        animation: waveAnim ? `${waveAnim}, ${baseAnim}` : baseAnim,
        transition: "transform 200ms ease, filter 200ms ease",
      };

  return (
    <span
      className={className}
      data-testid="qelli-face-avatar"
      style={{
        position: "relative",
        display:  "inline-block",
        width:    size,
        height:   size,
      }}
    >
      {/* The portrait itself — carries the breathe animation. */}
      <img
        src={QELLI_AVATAR_URL}
        alt=""
        draggable={false}
        style={{
          position: "absolute",
          inset: 0,
          width:  "100%",
          height: "100%",
          objectFit: "contain",
          userSelect: "none",
          transformOrigin: "center 55%",
          ...breatheStyle,
        }}
      />

      {/* Smile-warmth glow — sits above the portrait at the smile. */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left:   "36%",
          top:    "62%",
          width:  "28%",
          height: "12%",
          borderRadius: "50%",
          background: "radial-gradient(closest-side, rgba(0,229,255,0.55) 0%, rgba(0,229,255,0) 70%)",
          opacity: smileOn ? 0.9 : 0,
          transition: "opacity 600ms ease",
          pointerEvents: "none",
          mixBlendMode: "screen",
        }}
      />

      {/* Eyelid bars — snap shut on `blink`. */}
      {[ "36%", "64%" ].map((leftPct) => (
        <span
          aria-hidden="true"
          key={leftPct}
          style={{
            position: "absolute",
            left:   leftPct,
            top:    "47%",
            transform: `translate(-50%, -50%) scaleY(${blink ? 1 : 0})`,
            transformOrigin: "center",
            width:  lidWidth,
            height: lidHeight,
            background:
              `linear-gradient(180deg, ${lidColor} 0%, #0A8FB0 50%, ${lidColor} 100%)`,
            borderRadius: "30%",
            transition: "transform 80ms ease-in",
            boxShadow: blink ? "0 0 6px rgba(0,229,255,0.7)" : "none",
            pointerEvents: "none",
          }}
        />
      ))}
    </span>
  );
}


export default QelliFaceAvatar;
